<template>
  <v-container fluid class="pa-4 pa-md-6">
    <div class="d-flex align-center justify-space-between mb-4">
      <div>
        <div class="text-h5">{{ $t("listing_refresh.title") }}</div>
        <div class="text-caption text-medium-emphasis">
          {{ $t("listing_refresh.superadmin_only") }}
        </div>
      </div>
      <v-btn variant="outlined" :loading="loading" @click="load">
        {{ $t("listing_refresh.refresh") }}
      </v-btn>
    </div>

    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>

    <v-card v-if="overview" class="mb-4 pa-4">
      <v-row>
        <v-col cols="12" md="4">
          <div class="d-flex align-center">
            <v-switch
              :model-value="overview.global.enabled"
              color="success"
              hide-details
              :loading="savingGlobal"
              @update:model-value="saveGlobal({ enabled: $event })"
            />
            <div>
              <div class="text-subtitle-1">
                {{
                  overview.global.enabled
                    ? $t("listing_refresh.running")
                    : $t("listing_refresh.stopped")
                }}
              </div>
              <div class="text-caption">{{ $t("listing_refresh.master_switch") }}</div>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <div class="text-caption text-medium-emphasis">{{ $t("listing_refresh.schedule") }}</div>
          <div class="d-flex align-center mt-1">
            <v-text-field
              v-model.number="tickMinutes"
              type="number"
              min="1"
              max="59"
              density="compact"
              variant="outlined"
              hide-details
              style="max-width: 88px"
              :label="$t('listing_refresh.minutes')"
            />
            <v-btn
              class="ml-2"
              size="small"
              variant="outlined"
              :loading="savingGlobal"
              @click="saveInterval"
            >
              {{ $t("listing_refresh.save") }}
            </v-btn>
          </div>
          <div class="text-caption mt-1">
            {{ $t("listing_refresh.every_minutes", { minutes: overview.global.tickIntervalMinutes }) }}
          </div>
          <div class="text-caption">
            {{ $t("listing_refresh.next_tick") }} {{ formatTime(overview.global.nextTickAt) }}
          </div>
          <div class="text-caption">{{ $t("listing_refresh.interval_hint") }}</div>
          <div v-if="overview.global.locked" class="text-caption">
            {{ $t("listing_refresh.tick_in_progress") }}
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <div class="text-caption text-medium-emphasis">{{ $t("listing_refresh.trading_today") }}</div>
          <div>{{ overview.global.callsToday }} / {{ overview.global.callsBudget }}</div>
          <v-progress-linear
            class="mt-2"
            :model-value="budgetPercent"
            color="primary"
            height="8"
            rounded
          />
        </v-col>
      </v-row>
    </v-card>

    <v-row v-if="overview" class="mb-2">
      <v-col cols="12" sm="6" md="3">
        <v-card class="pa-4">
          <div class="text-caption">{{ $t("listing_refresh.refreshed_today") }}</div>
          <div class="text-h5">{{ overview.refreshedToday }}</div>
          <div class="text-caption">
            {{ $t("listing_refresh.of_cap", { cap: overview.capToday }) }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="pa-4">
          <div class="text-caption">{{ $t("listing_refresh.queue_remaining") }}</div>
          <div class="text-h5">{{ overview.queueParts }}</div>
          <div class="text-caption">{{ $t("listing_refresh.parts_waiting") }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="pa-4">
          <div class="text-caption">{{ $t("listing_refresh.errors_today") }}</div>
          <div class="text-h5">{{ overview.errorsToday }}</div>
          <div class="text-caption">
            {{ overview.errorSplit.OFF_EBAY_ATTENTION }} {{ $t("listing_refresh.off_ebay") }},
            {{ overview.errorSplit.END_PARTIAL_RESTORED }} {{ $t("listing_refresh.partial") }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="pa-4">
          <div class="text-caption">{{ $t("listing_refresh.needs_attention") }}</div>
          <div class="text-h5">{{ attentionCount }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-card v-if="overview" class="mb-4">
      <div class="d-flex align-center justify-space-between pa-4">
        <div class="text-subtitle-1">{{ $t("listing_refresh.institutions") }}</div>
        <v-btn color="red-darken-3" @click="addDialog = true">
          {{ $t("listing_refresh.add_institution") }}
        </v-btn>
      </div>
      <v-data-table
        :headers="headers"
        :items="overview.institutions"
        :loading="loading"
        item-value="institutionId"
      >
        <template #item.title="{ item }">
          <router-link :to="{ name: 'listing-refresh-institution', params: { institutionId: item.institutionId } }">
            {{ item.title || item.institutionId }}
          </router-link>
          <div class="text-caption">{{ (item.sites || []).join(", ") }}</div>
        </template>
        <template #item.status="{ item }">
          <v-chip size="small" :color="statusColor(item.status)">
            {{ statusLabel(item) }}
          </v-chip>
        </template>
        <template #item.daily="{ item }">
          {{ item.daily.done }} / {{ item.dailyCap }}
        </template>
        <template #item.errors="{ item }">
          {{ item.daily.errors }}
        </template>
        <template #item.progress="{ item }">
          <v-progress-linear :model-value="item.progress" height="8" rounded color="primary" />
          <span class="text-caption">{{ item.progress }}%</span>
        </template>
        <template #item.indexCursor="{ item }">
          {{ formatDate(item.indexCursor) }}
        </template>
      </v-data-table>
    </v-card>

    <v-card v-if="overview" class="pa-4">
      <div class="text-subtitle-1 mb-2">{{ $t("listing_refresh.needs_attention") }}</div>
      <div v-if="!overview.attention.length && !overview.stuck.length" class="text-medium-emphasis">
        {{ $t("listing_refresh.none") }}
      </div>
      <div v-for="row in overview.attention" :key="row.id" class="mb-2">
        <v-chip size="small" class="mr-2" :color="statusColor(row.status)">{{ outcomeLabel(row.status) }}</v-chip>
        {{ row.partTitle || row.partId }}
        <span class="text-caption ml-2">{{ formatTime(row.createdAt) }}</span>
      </div>
      <div v-for="row in overview.stuck" :key="row.itemId" class="mb-2">
        <v-chip size="small" class="mr-2" color="grey">{{ $t("listing_refresh.stuck") }}</v-chip>
        {{ row.partTitle || row.partId }}
        <span class="text-caption ml-2">{{ row.itemId }}</span>
      </div>
    </v-card>

    <v-dialog v-model="addDialog" max-width="480">
      <v-card class="pa-4">
        <div class="text-subtitle-1 mb-3">{{ $t("listing_refresh.add_institution") }}</div>
        <v-autocomplete
          v-model="newInstitutionId"
          :items="institutions"
          :loading="loadingInstitutions"
          item-title="title"
          item-value="_id"
          :label="$t('listing_refresh.institution')"
          variant="outlined"
          density="compact"
        />
        <v-alert v-if="addError" type="error" variant="tonal" class="mb-3">{{ addError }}</v-alert>
        <div class="d-flex justify-end">
          <v-btn variant="text" @click="addDialog = false">{{ $t("listing_refresh.cancel") }}</v-btn>
          <v-btn color="red-darken-3" :loading="adding" @click="addInstitution">
            {{ $t("listing_refresh.save") }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import getRequest from "@/api/getRequest";
import postRequest from "@/api/postRequest";
import putRequest from "@/api/putRequest";

export default {
  name: "ListingRefresh",
  data() {
    return {
      overview: null,
      loading: false,
      error: "",
      savingGlobal: false,
      addDialog: false,
      institutions: [],
      loadingInstitutions: false,
      newInstitutionId: null,
      adding: false,
      addError: "",
      tickMinutes: 20,
    };
  },
  computed: {
    headers() {
      return [
        { title: this.$t("listing_refresh.institution"), key: "title" },
        { title: this.$t("listing_refresh.status"), key: "status" },
        { title: this.$t("listing_refresh.today"), key: "daily" },
        { title: this.$t("listing_refresh.errors"), key: "errors" },
        { title: this.$t("listing_refresh.queue_left"), key: "queueParts" },
        { title: this.$t("listing_refresh.progress"), key: "progress" },
        { title: this.$t("listing_refresh.indexed_up_to"), key: "indexCursor" },
      ];
    },
    budgetPercent() {
      if (!this.overview || !this.overview.global.callsBudget) return 0;
      return Math.min(100, Math.round((this.overview.global.callsToday / this.overview.global.callsBudget) * 100));
    },
    attentionCount() {
      if (!this.overview) return 0;
      return this.overview.attention.length + this.overview.stuck.length;
    },
  },
  watch: {
    addDialog(open) {
      if (open && !this.institutions.length) this.loadInstitutions();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.error = "";
      getRequest("/listing-refresh")
        .then((data) => {
          this.overview = data;
          this.tickMinutes = data.global.tickIntervalMinutes;
        })
        .catch((err) => {
          this.error = (err.response && err.response.data && err.response.data.errorMsg) || err.message;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadInstitutions() {
      this.loadingInstitutions = true;
      getRequest("/institution/")
        .then((data) => {
          const rows = Array.isArray(data) ? data : data.institutions || [];
          this.institutions = rows.filter((row) => row.export_ebay_leko === true);
        })
        .finally(() => {
          this.loadingInstitutions = false;
        });
    },
    saveInterval() {
      const minutes = Number(this.tickMinutes);
      if (!Number.isFinite(minutes) || minutes < 1 || minutes > 59) {
        this.error = this.$t("listing_refresh.interval_invalid");
        return;
      }
      this.saveGlobal({ tickIntervalMinutes: Math.floor(minutes) });
    },
    saveGlobal(body) {
      this.savingGlobal = true;
      putRequest("/listing-refresh/global", "", body)
        .then(() => this.load())
        .catch((err) => {
          this.error = (err.response && err.response.data && err.response.data.errorMsg) || err.message;
        })
        .finally(() => {
          this.savingGlobal = false;
        });
    },
    addInstitution() {
      this.adding = true;
      this.addError = "";
      postRequest
        .object("/listing-refresh/institutions", { institutionId: this.newInstitutionId })
        .then(() => {
          this.addDialog = false;
          this.newInstitutionId = null;
          this.load();
        })
        .catch((err) => {
          this.addError = (err.response && err.response.data && err.response.data.errorMsg) || err.message;
        })
        .finally(() => {
          this.adding = false;
        });
    },
    statusLabel(item) {
      if (item.status === "paused") {
        return `${this.$t("listing_refresh.paused")}${item.pauseReason ? ` · ${item.pauseReason}` : ""}`;
      }
      return this.$t(`listing_refresh.status_${item.status}`);
    },
    statusColor(status) {
      if (status === "live" || status === "DONE") return "success";
      if (status === "dry_run" || status === "DRY_RUN") return "warning";
      if (status === "OFF_EBAY_ATTENTION" || status === "paused") return "error";
      return "grey";
    },
    outcomeLabel(status) {
      const key = `listing_refresh.outcome_${status}`;
      const label = this.$t(key);
      return label === key ? status : label;
    },
    formatDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleString();
    },
    formatTime(value) {
      if (!value) return "—";
      return new Date(value).toLocaleString();
    },
  },
};
</script>
